import moment from 'moment';

import preloaded from '~/preloaded';
import settings from '~/settings';

import { ONBOARDING_GUIDES, ONBOARDING_GUIDES_CONFIG } from '~/const/onboarding';
import { DWH_EVENTS } from '~/dwhExport';
import { clanstarsIsEnabled } from '~/helpers/common';
import { getLastBrawlSeason, getLastRegularSeason } from '~/helpers/ladder';
import { t } from '~/helpers/localization';
import { hasPermission, PERMISSIONS, ROLE_NAMES } from '~/roles';
import { ROUTES_BADGES, ROUTES_MAP } from '~/router/routes';
import { CLAN_WARS_STAGE } from '~/Reducers/ReducerClanWars';

import iconClanBattles from './images/tile_icon_clan_battles.png';
import iconClanManagement from './images/tile_icon_clan_management_large.png';
import iconClanProfile from './images/tile_icon_clan_profile_large.png';
import iconClanStars from './images/tile_icon_clan_stars.png';
import iconNavalBattle from './images/tile_icon_naval_battle.png';

import type React from 'react';
import type { NavigationContext } from '~/store/selectors/navigationSelectors';

export interface HubTileConfig {
  id: string;
  title: string;
  subtitle?: string;
  description?: (roleName: ROLE_NAMES | null) => string;
  icon?: string;

  to: string | ((navigationContext: NavigationContext) => string);

  isVisible?: (navigationContext: NavigationContext) => boolean;
  isEnabled?: (navigationContext: NavigationContext) => boolean;

  endsAt?: (navigationContext: NavigationContext) => number | null;
  endsAtTextLabel?: (navigationContext: NavigationContext) => string | null;

  warningBadge?: (navigationContext: NavigationContext) => string | null;
  actionBadge?: React.ReactNode;

  badges?: ROUTES_BADGES[];

  onboarding?: (roleName: ROLE_NAMES | null) => {
    onboardingStep: ONBOARDING_GUIDES;
    guidingtip: (typeof ONBOARDING_GUIDES_CONFIG)[keyof typeof ONBOARDING_GUIDES_CONFIG];
  };

  dwhEvents?: {
    click?: string;
  };
}

export enum HUB_TILE {
  ABOUT = 'about',
  CLAN_WARS = 'clan_wars',
  CLAN_BATTLES = 'clan_battles',
  CLAN_MANAGEMENT = 'clan_management',
  CLAN_PROFILE = 'clan_profile',
  CLAN_STARS = 'clan_stars',
  CREATE_CLAN = 'create_clan',
}

export const HUB_TILES: Record<HUB_TILE, HubTileConfig> = {
  [HUB_TILE.ABOUT]: {
    id: HUB_TILE.ABOUT,
    title: t('About Clans'),
    subtitle: t('Discover Clans and find the right one for you'),
    description: () =>
      t('Discover Clans and find like-minded players to fight together, unlock new activities, and earn bonuses.'),
    to: ROUTES_MAP.ABOUT,
    badges: [ROUTES_BADGES.REQUESTS],
    icon: iconClanProfile,
    isVisible: ({ currentAccount }) => {
      return !currentAccount?.clanId;
    },
    actionBadge: t('Discover Clans'),
    dwhEvents: {
      click: DWH_EVENTS.CLANHUB.TILE_ABOUT_CLANS_CLICK,
    },
  },
  [HUB_TILE.CLAN_BATTLES]: {
    id: HUB_TILE.CLAN_BATTLES,
    title: t('Clan Battles'),
    subtitle: t('The season has not started yet'),
    description: () =>
      t('Face another Clan Division with ships of the same tier.\n\nAvailable during the Clan Battles season.'),
    to: ROUTES_MAP.CLAN_BATTLES,
    icon: iconClanBattles,
    isVisible: () => {
      return !!(getLastRegularSeason() || getLastBrawlSeason());
    },
    endsAt: () => {
      const season = getLastRegularSeason() || getLastBrawlSeason();
      if (!season?.finish_time) {
        return null;
      }

      return season.finish_time * 1e3;
    },
    dwhEvents: {
      click: DWH_EVENTS.CLANHUB.TILE_CLAN_BATTLES_CLICK,
    },
  },
  [HUB_TILE.CLAN_MANAGEMENT]: {
    id: HUB_TILE.CLAN_MANAGEMENT,
    title: t('Your Clan'),
    subtitle: t('Clan members and missions'),
    description: (roleName: ROLE_NAMES | null) => {
      if (roleName === ROLE_NAMES.RECRUITMENT_OFFICER) {
        return t(`Manage your Clan's requests and invites, compare your Clan with others, and track missions.`);
      }

      if (hasPermission(roleName, PERMISSIONS.HANDLE_INVITES)) {
        return t(
          `Manage your Clan's members, requests and invites, allocate resources, compare your Clan with others, and track missions.`,
        );
      }

      return t(`View your Clan's members and Treasury, compare your clan with others, and track missions.`);
    },
    to: ROUTES_MAP.MEMBERS,
    badges: [
      ROUTES_BADGES.REQUESTS,
      ROUTES_BADGES.TASKS,
      ROUTES_BADGES.MEMBERS,
      ROUTES_BADGES.SEARCH,
      ROUTES_BADGES.RECOMMENDATIONS_NEW,
    ],
    icon: iconClanManagement,
    isVisible: ({ currentAccount }) => {
      return !!currentAccount.clanId;
    },
    onboarding: (roleName: ROLE_NAMES | null) => {
      if (roleName === ROLE_NAMES.RECRUITMENT_OFFICER) {
        return {
          onboardingStep: ONBOARDING_GUIDES.CLANHUB_NAV_CLAN_MANAGEMENT,
          guidingtip: ONBOARDING_GUIDES_CONFIG.CLANHUB_NAV_CLAN_MANAGEMENT_RECRUITER,
        };
      }

      if (hasPermission(roleName, PERMISSIONS.HANDLE_INVITES)) {
        return {
          onboardingStep: ONBOARDING_GUIDES.CLANHUB_NAV_CLAN_MANAGEMENT,
          guidingtip: ONBOARDING_GUIDES_CONFIG.CLANHUB_NAV_CLAN_MANAGEMENT_CAN_HANDLE_INVITES,
        };
      }

      return {
        onboardingStep: ONBOARDING_GUIDES.CLANHUB_NAV_CLAN_MANAGEMENT,
        guidingtip: ONBOARDING_GUIDES_CONFIG.CLANHUB_NAV_CLAN_MANAGEMENT,
      };
    },
    dwhEvents: {
      click: DWH_EVENTS.CLANHUB.TILE_CLAN_MANAGEMENT_CLICK,
    },
  },
  [HUB_TILE.CLAN_PROFILE]: {
    id: HUB_TILE.CLAN_PROFILE,
    title: t('Clan Base'),
    subtitle: t('Clan development and bonuses'),
    badges: [ROUTES_BADGES.PROFILE],
    description: () =>
      t('The Clan Base grants bonuses and benefits. The more developed the Base is, the greater they become.'),
    to: ROUTES_MAP.PROFILE,
    icon: iconClanProfile,
    isVisible: ({ currentAccount }) => {
      return !!currentAccount.clanId;
    },
    onboarding: () => ({
      onboardingStep: ONBOARDING_GUIDES.CLANHUB_NAV_NAVAL_BASE,
      guidingtip: ONBOARDING_GUIDES_CONFIG.CLANHUB_NAV_NAVAL_BASE,
    }),
    dwhEvents: {
      click: DWH_EVENTS.CLANHUB.TILE_NAVALBASE_CLICK,
    },
  },
  [HUB_TILE.CLAN_STARS]: {
    id: HUB_TILE.CLAN_STARS,
    title: t('Звезда отряда'),
    badges: [ROUTES_BADGES.CLAN_STARS],
    description: () => t('Fight in a Division together with your Clanmates and earn rewards.'),
    to: ROUTES_MAP.CLAN_STARS,
    icon: iconClanStars,
    isVisible: ({ currentAccount }) => {
      return clanstarsIsEnabled && !!currentAccount.clanId;
    },
    endsAt: () => {
      const endsAt = preloaded.clanStarsSeasonSettings?.seasonDateFinish;
      if (!endsAt) {
        return null;
      }

      return endsAt * 1e3;
    },
    onboarding: () => ({
      onboardingStep: ONBOARDING_GUIDES.CLANSTARS_REFINED,
      guidingtip: ONBOARDING_GUIDES_CONFIG.CLANSTARS_REFINED,
    }),
    dwhEvents: {
      click: DWH_EVENTS.CLANHUB.TILE_CLAN_STARS_CLICK,
    },
  },
  [HUB_TILE.CREATE_CLAN]: {
    id: HUB_TILE.CREATE_CLAN,
    title: t('Create a Clan'),
    subtitle: t('Start your own Clan'),
    badges: [ROUTES_BADGES.CREATE],
    description: () => t('Lead like-minded players into battles, and unlock activities, resources, and bonuses.'),
    to: ROUTES_MAP.CLAN_CREATE,
    icon: iconClanManagement,
    isVisible: ({ currentAccount }) => {
      return !currentAccount.id || (!!currentAccount.id && !currentAccount.clanId);
    },
    isEnabled: ({ currentAccount }) => {
      return !!currentAccount.id;
    },
    warningBadge: ({ currentAccount }) => {
      if (!currentAccount.id) {
        return t('Log in to create a Clan');
      }
      return null;
    },
    actionBadge: t('Create a Clan'),
    dwhEvents: {
      click: DWH_EVENTS.CLANHUB.TILE_CREATE_CLAN_CLICK,
    },
  },
  [HUB_TILE.CLAN_WARS]: {
    id: HUB_TILE.CLAN_WARS,
    title: t('Морское сражение'),
    subtitle: t('Join Naval Battles'),
    description: () => t('Compete in Clan vs Clan battles and earn Oil.\n\nHeld weekly across several stages.'),
    to: ROUTES_MAP.CLAN_WARS,
    icon: iconNavalBattle,
    isVisible: () => {
      return settings.cwars.isEnabled;
    },
    endsAt: ({ clanWarsCurrentStage, clanWarsSetingsStages }) => {
      if (clanWarsCurrentStage === CLAN_WARS_STAGE.WAR) {
        return moment.utc(clanWarsSetingsStages.war.endDate).valueOf();
      }

      if (clanWarsCurrentStage === CLAN_WARS_STAGE.PREPARATION) {
        return moment.utc(clanWarsSetingsStages.preparation.endDate).valueOf();
      }

      if (clanWarsCurrentStage === CLAN_WARS_STAGE.MATCHMAKING) {
        return moment.utc(clanWarsSetingsStages.war.startDate).valueOf();
      }

      if (clanWarsCurrentStage === CLAN_WARS_STAGE.REWARDING) {
        return moment.utc(clanWarsSetingsStages.rewarding.endDate).valueOf();
      }

      return null;
    },
    endsAtTextLabel: ({ clanWarsCurrentStage }) => {
      if (clanWarsCurrentStage === CLAN_WARS_STAGE.WAR) {
        return t('Engagement ends in %(timeleft)s');
      }

      if (clanWarsCurrentStage === CLAN_WARS_STAGE.PREPARATION) {
        return t('Preparation ends in %(timeleft)s');
      }

      if (clanWarsCurrentStage === CLAN_WARS_STAGE.MATCHMAKING) {
        return t('Opponent selection ends in %(timeleft)s');
      }

      if (clanWarsCurrentStage === CLAN_WARS_STAGE.REWARDING) {
        return t('Results available in %(timeleft)s');
      }

      return null;
    },
    dwhEvents: {
      click: DWH_EVENTS.CLANHUB.TILE_CLAN_WARS_CLICK,
    },
  },
};

export enum HUB_TILES_PRESET {
  PLAYER_WITH_CLAN = 'player_with_clan',
  PLAYER_WITHOUT_CLAN = 'player_without_clan',
  NOT_LOGGED_IN = 'not_logged_in',
}

export const HUB_TILES_PRESETS: Record<HUB_TILES_PRESET, { MAIN: HUB_TILE[]; ASIDE: HUB_TILE[] }> = {
  [HUB_TILES_PRESET.PLAYER_WITH_CLAN]: {
    MAIN: [HUB_TILE.CLAN_PROFILE, HUB_TILE.CLAN_MANAGEMENT],
    ASIDE: [HUB_TILE.CLAN_WARS, HUB_TILE.CLAN_BATTLES, HUB_TILE.CLAN_STARS],
  },
  [HUB_TILES_PRESET.PLAYER_WITHOUT_CLAN]: {
    MAIN: [HUB_TILE.ABOUT, HUB_TILE.CREATE_CLAN],
    ASIDE: [HUB_TILE.CLAN_WARS, HUB_TILE.CLAN_BATTLES],
  },
  [HUB_TILES_PRESET.NOT_LOGGED_IN]: {
    MAIN: [HUB_TILE.ABOUT, HUB_TILE.CREATE_CLAN],
    ASIDE: [HUB_TILE.CLAN_WARS, HUB_TILE.CLAN_BATTLES],
  },
};
